import { Link } from 'react-router-dom';

import { reasonLabel } from '../shared/labels';
import { dueText } from './overviewModel';
import { useLifecycleToast } from './Toast';
import { C, Icon, OutlineButton, PATHS, Plain, text14 } from './ui';

import styled, { keyframes } from '@/styles';
import { formatPhone } from '@/routes/Agency/GravyWorkers/mappings';
import { CallTask } from '@/services/lifecycleAPI';

export const CALL_GRID = '100px 140px 140px 120px minmax(0, 1fr)';

// A logged row shows "Logged", then slides up and out of the list.
export const LOGGED_EXIT_MS = 900;
const swipeUp = keyframes({
  '0%, 35%': { transform: 'translateY(0)', opacity: 1, maxHeight: 160 },
  '75%': {
    transform: 'translateY(-60%)',
    opacity: 0,
    maxHeight: 160,
    paddingBottom: 12,
  },
  '100%': {
    transform: 'translateY(-60%)',
    opacity: 0,
    maxHeight: 0,
    paddingBottom: 0,
    borderBottomWidth: 0,
  },
});

const DontCall = styled('button', {
  height: 36,
  padding: '8px 16px',
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'inherit',
  fontSize: 14,
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  borderRadius: 8,
  border: 'none',
  background: C.white,
  fontWeight: 600,
  boxShadow: `inset 0 0 0 1px ${C.stroke}`,
  color: C.red,
  cursor: 'pointer',
});

type Props = {
  tasks: CallTask[];
  loading: boolean;
  error: string | null;
  logged: number[];
  gone: number[];
  openCount: number;
  onLog: (task: CallTask) => void;
  onRetry: () => void;
};

const cell = { padding: '4px 12px 4px 0' } as const;

const CallTasksPanel = ({
  tasks,
  loading,
  error,
  logged,
  gone,
  openCount,
  onLog,
  onRetry,
}: Props) => {
  const { toast } = useLifecycleToast();
  const now = new Date();
  const shown = tasks.filter((task) => !gone.includes(task.id));
  const shownOpen = tasks.filter((task) => !logged.includes(task.id)).length;
  const more = openCount > shownOpen;

  return (
    <div
      data-testid="callTasksPanel"
      style={{ display: 'flex', flexDirection: 'column', gap: 12, ...text14 }}
    >
      <div
        role="row"
        style={{
          display: 'grid',
          gridTemplateColumns: CALL_GRID,
          alignItems: 'start',
          paddingBottom: 12,
          borderBottom: `1px solid ${C.stroke}`,
          fontWeight: 800,
          color: C.grey,
        }}
      >
        <span style={{ padding: '4px 0' }}>Due</span>
        <span style={{ padding: '4px 0' }}>Worker info</span>
        <span style={{ padding: '4px 0' }}>Phone number</span>
        <span style={{ padding: '4px 0' }}>Assigned to</span>
        <span />
      </div>
      {loading && (
        <div style={{ padding: '24px 0', textAlign: 'center', color: C.grey }}>
          Loading call tasks...
        </div>
      )}
      {error && (
        <div
          role="alert"
          style={{ padding: '24px 0', textAlign: 'center', color: C.red }}
        >
          {error}{' '}
          <Plain
            type="button"
            style={{ color: C.green, fontWeight: 600 }}
            onClick={onRetry}
          >
            Try again
          </Plain>
        </div>
      )}
      {!loading && !error && shown.length === 0 && !more && (
        <div
          data-testid="callTasksEmpty"
          style={{ padding: '24px 0', textAlign: 'center', color: C.grey }}
        >
          All calls are done for this journey.
        </div>
      )}
      {!loading && !error && more && shownOpen > 0 && (
        <div data-testid="callTasksMore" style={{ color: C.grey }}>
          {`Showing ${shownOpen} of ${openCount} open calls.`}
        </div>
      )}
      {shown.map((task, index) => {
        const due = dueText(task, now);
        const done = logged.includes(task.id);
        const last = index === shown.length - 1;
        return (
          <div
            data-testid={`callTaskRow-${task.id}`}
            key={task.id}
            style={{
              display: 'grid',
              gridTemplateColumns: CALL_GRID,
              alignItems: 'start',
              paddingBottom: 12,
              borderBottom: last ? 'none' : `1px solid ${C.stroke}`,
              overflow: 'hidden',
              animation: done
                ? `${swipeUp} ${LOGGED_EXIT_MS}ms cubic-bezier(.4,0,.2,1) forwards`
                : 'none',
            }}
          >
            <span
              style={{
                ...cell,
                whiteSpace: 'pre-line',
                fontWeight: due.overdue ? 600 : 400,
                color: due.overdue ? C.red : C.black,
              }}
            >
              {due.text}
            </span>
            <div style={{ ...cell, display: 'flex', flexDirection: 'column' }}>
              <Link
                style={{
                  fontWeight: 600,
                  color: C.green,
                  textDecoration: 'none',
                }}
                to={`/workers_v2/${task.worker.id}`}
              >
                {task.worker.name}
              </Link>
              <span style={{ color: C.grey }}>{task.worker.city ?? ''}</span>
            </div>
            <span
              style={{
                ...cell,
                color: task.worker.phone_number ? C.black : C.light,
              }}
            >
              {task.worker.phone_number
                ? formatPhone(task.worker.phone_number)
                : 'No phone number'}
            </span>
            <span
              style={{
                ...cell,
                color: task.assigned_to_name ? C.black : C.light,
              }}
            >
              {task.assigned_to_name ?? 'No assignee yet'}
            </span>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '4px 0',
              }}
            >
              {done ? (
                <div
                  data-testid={`callTaskLogged-${task.id}`}
                  style={{
                    height: 36,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    fontWeight: 600,
                    color: C.green,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Icon color={C.green} d={PATHS.check} size={16} width={2} />
                  <span>Logged</span>
                </div>
              ) : task.do_not_call_reason ? (
                <DontCall
                  data-testid={`callTaskDontCall-${task.id}`}
                  title={`Do not call · ${reasonLabel(task.do_not_call_reason ?? '').label}`}
                  type="button"
                  onClick={() =>
                    toast(
                      'Don’t call',
                      `Do not call ${task.worker.name}: ${reasonLabel(task.do_not_call_reason ?? '').label}.`,
                    )
                  }
                >
                  Don’t call
                </DontCall>
              ) : (
                <OutlineButton
                  data-testid={`callTaskLog-${task.id}`}
                  type="button"
                  onClick={() => onLog(task)}
                >
                  Log call
                </OutlineButton>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CallTasksPanel;
